import type { IntelligenceAnalysis, AgentState } from '../../types/api';
import MatchScore from './MatchScore';
import EvidenceSection from './EvidenceSection';

interface IntelligenceBriefProps {
  analysis: IntelligenceAnalysis;
  agents: AgentState[];
}

function RecommendationBadge({ recommendation }: { recommendation: IntelligenceAnalysis['recommendation'] }) {
  const config: Record<
    IntelligenceAnalysis['recommendation'],
    { label: string; bg: string; text: string; border: string }
  > = {
    exceptional: {
      label: 'EXCEPTIONAL FIT',
      bg: 'bg-ti-green/20',
      text: 'text-ti-green',
      border: 'border-ti-green/40',
    },
    'strong-fit': {
      label: 'STRONG FIT',
      bg: 'bg-ti-green/15',
      text: 'text-ti-green',
      border: 'border-ti-green/30',
    },
    'good-fit': {
      label: 'GOOD FIT',
      bg: 'bg-ti-blue/10',
      text: 'text-ti-blue',
      border: 'border-ti-blue/20',
    },
    'weak-fit': {
      label: 'WEAK FIT',
      bg: 'bg-ti-amber/10',
      text: 'text-ti-amber',
      border: 'border-ti-amber/20',
    },
    'poor-fit': {
      label: 'POOR FIT',
      bg: 'bg-ti-red/10',
      text: 'text-ti-red',
      border: 'border-ti-red/20',
    },
  };

  const c = config[recommendation];

  return (
    <span
      className={`inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest border ${c.bg} ${c.text} ${c.border}`}
    >
      {c.label}
    </span>
  );
}

function SeverityBadge({ severity }: { severity: 'critical' | 'moderate' | 'minor' }) {
  const config: Record<string, { bg: string; text: string }> = {
    critical: { bg: 'bg-ti-red/10', text: 'text-ti-red' },
    moderate: { bg: 'bg-ti-amber/10', text: 'text-ti-amber' },
    minor: { bg: 'bg-ti-text-dim/10', text: 'text-ti-text-dim' },
  };

  const c = config[severity];

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${c.bg} ${c.text}`}>
      {severity}
    </span>
  );
}

export default function IntelligenceBrief({ analysis, agents }: IntelligenceBriefProps) {
  const totalEndpoints = agents.reduce((sum, a) => sum + a.endpoints.length, 0);
  const totalTime = agents.reduce((max, a) => {
    if (a.endTime && a.startTime) {
      return Math.max(max, a.endTime - a.startTime);
    }
    return max;
  }, 0);

  return (
    <div className="space-y-6">
      {/* Header — classified document style */}
      <div className="border border-ti-border rounded-lg overflow-hidden">
        <div className="bg-ti-surface px-6 py-4 border-b border-ti-border">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <div className="w-1 h-8 bg-ti-blue rounded-full" />
                <div>
                  <h2 className="text-lg font-bold text-ti-text tracking-tight">
                    Intelligence Brief
                  </h2>
                  <p className="text-xs text-ti-text-dim font-mono uppercase tracking-widest">
                    Candidate Assessment // Classified
                  </p>
                </div>
              </div>
            </div>
            <div className="text-right text-xs text-ti-text-dim font-mono space-y-0.5">
              <div>{totalEndpoints} endpoints queried</div>
              <div>{totalTime}ms total collection time</div>
            </div>
          </div>
        </div>

        {/* Score + Recommendation row */}
        <div className="px-6 py-6 flex flex-col sm:flex-row items-center gap-6 border-b border-ti-border/50">
          <MatchScore score={analysis.match_score} />
          <div className="flex flex-col items-center sm:items-start gap-3">
            <RecommendationBadge recommendation={analysis.recommendation} />
            <p className="text-xs text-ti-text-dim text-center sm:text-left">
              Based on multi-source intelligence analysis across {totalEndpoints} data endpoints
            </p>
          </div>
        </div>

        {/* Executive Summary */}
        <div className="px-6 py-5 border-b border-ti-border/50">
          <h3 className="text-xs font-semibold text-ti-text-muted uppercase tracking-wider mb-3">
            Executive Summary
          </h3>
          <div className="text-sm text-ti-text leading-relaxed space-y-4">
            {analysis.executive_summary.split('\n\n').map((paragraph, i) => (
              <p key={i}>{paragraph}</p>
            ))}
          </div>
        </div>

        {/* Strengths */}
        <div className="px-6 py-5 border-b border-ti-border/50">
          <EvidenceSection
            title="Verified Strengths"
            items={analysis.strengths.map((s) => ({
              claim: s.claim,
              evidence: s.evidence,
              source_endpoint: s.source_endpoint,
            }))}
          />
        </div>

        {/* Gaps */}
        <div className="px-6 py-5 border-b border-ti-border/50">
          <h3 className="text-xs font-semibold text-ti-text-muted uppercase tracking-wider mb-3">
            Identified Gaps
          </h3>
          <div className="space-y-3">
            {analysis.gaps.map((gap, i) => (
              <div
                key={i}
                className="flex items-start gap-3 bg-ti-bg/50 rounded-lg p-4"
              >
                <SeverityBadge severity={gap.severity} />
                <div className="flex-1">
                  <p className="text-sm font-medium text-ti-text">{gap.gap}</p>
                  <p className="text-xs text-ti-text-muted mt-1">
                    Mitigation: {gap.mitigation}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Cultural Alignment */}
        <div className="px-6 py-5 border-b border-ti-border/50">
          <h3 className="text-xs font-semibold text-ti-text-muted uppercase tracking-wider mb-3">
            Cultural Alignment
          </h3>
          <p className="text-sm text-ti-text leading-relaxed">
            {analysis.cultural_alignment}
          </p>
        </div>

      </div>
    </div>
  );
}
